import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import FormatIForm from "./components/FormatIForm";
import FormatIIForm from "./components/FormatIIForm";
import FormatIIIForm from "./components/FormatIIIForm";
import FormatIIIAForm from "./components/FormatIIIAForm";
import FormatIVForm from "./components/FormatIVForm";
import { Authorization } from "../../Context/Authorization";
import { Form, Button, Row, Col, ProgressBar } from "react-bootstrap";

const HalfYearlyReportForm = () => {
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);

  const formatSteps = [
    "FORMAT-I",
    "FORMAT-II",
    "FORMAT-III",
    "FORMAT-III(A)",
    "FORMAT-IV",
  ];
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFormat, setSelectedFormat] = useState(formatSteps[0]);
  const [savedSteps, setSavedSteps] = useState([]);

  const Api = process.env.REACT_APP_API;
  const [formData, setFormData] = useState({
    year: "",
    halfYear: "H1",
    circle: "",
    division: "",
    range: "",
    formatI: {},
    formatII: {},
    formatIII: [],
    formatIIIA: [],
    formatIV: [],
  });

  // Refs for each format
  const formatRefs = {
    "FORMAT-I": useRef(),
    "FORMAT-II": useRef(),
    "FORMAT-III": useRef(),
    "FORMAT-III(A)": useRef(),
    "FORMAT-IV": useRef(),
  };

  const fetchDivision = async () => {
    const config = Authorization();
    try {
      const response = await axios.get(`${Api}/api/divisions`, config);
      if (Array.isArray(response.data)) {
        setDivisions(response.data);
      }
    } catch (err) {
      console.error("Error fetching divisions", err);
    }
  };

  const fetchRanges = async (divisionId) => {
    const config = Authorization();
    try {
      const response = await axios.post(
        `${Api}/api/ranges/ranges-by-division`,
        { divisionId },
        config
      );
      setRanges(response.data);
    } catch (error) {
      console.error("Error fetching ranges:", error);
    }
  };

  const fetchVfcs = async (rangeId) => {
    const config = Authorization();
    try {
      const response = await axios.post(
        `${Api}/api/vfcs/vfcs-by-range`,
        { rangeId },
        config
      );
      setVfcs(response.data.vfcs);
    } catch (error) {
      console.error("Error fetching VFCs:", error);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData((prev) => {
      if (["year", "halfYear", "circle", "division", "range"].includes(field)) {
        return { ...prev, [field]: value };
      } else {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        };
      }
    });
  };

  const handleSave = () => {
    const currentFormat = formatSteps[currentStep];
    const ref = formatRefs[currentFormat];

    if (ref.current && typeof ref.current.validate === "function") {
      const isValid = ref.current.validate();
      if (!isValid) {
        alert("Please fill all the details before saving.");
        return;
      }
    }

    if (!savedSteps.includes(currentFormat)) {
      setSavedSteps([...savedSteps, currentFormat]);
    }
  };

  const handleContinue = () => {
    const nextStep = currentStep + 1;
    if (nextStep < formatSteps.length) {
      setCurrentStep(nextStep);
      setSelectedFormat(formatSteps[nextStep]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run validation on all steps before submit
    for (const step of formatSteps) {
      const ref = formatRefs[step];
      if (ref.current && typeof ref.current.validate === "function") {
        const isValid = ref.current.validate();
        if (!isValid) {
          alert(`Validation failed in ${step}. Please check your entries.`);
          return;
        }
      }
    }

    try {
      await axios.post(
        `${Api}/api/half-yearly-report`,
        formData,
        Authorization()
      );
      alert("Report submitted successfully!");
      setFormData({
        year: "",
        halfYear: "H1",
        circle: "",
        division: "",
        range: "",
        formatI: {},
        formatII: {},
        formatIII: [],
        formatIIIA: [],
        formatIV: [],
      });
      setCurrentStep(0);
      setSelectedFormat(formatSteps[0]);
      setSavedSteps([]);
    } catch (err) {
      console.error("Error submitting report", err);
    }
  };

  useEffect(() => {
    fetchDivision();
    setSelectedFormat(formatSteps[0]);
  }, []);

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title">
          Half Yearly Progress Report - Step {currentStep + 1}
        </h3>
      </div>

      <ProgressBar
        now={(currentStep + 1) * 20}
        label={`${selectedFormat}`}
        className="mb-4"
      />

      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Half Year</Form.Label>
              <Form.Select
                value={formData.halfYear}
                onChange={(e) => handleChange("", "halfYear", e.target.value)}
              >
                <option value="H1">H1 (Jan–Jun)</option>
                <option value="H2">H2 (Jul–Dec)</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Division</Form.Label>
              <Form.Select
                value={formData.division}
                onChange={(e) => {
                  const selectedDiv = divisions.find(
                    (d) => d._id === e.target.value
                  );

                  const divisionId = selectedDiv?._id;

                  // Set the main division field
                  handleChange("", "division", divisionId);

                  // Set division in all format sections
                  handleChange("formatI", "nameOfDivision", divisionId);
                  handleChange("formatII", "nameOfDivision", divisionId);
                  handleChange("formatIII", "nameOfDivision", divisionId);
                  handleChange("formatIIIA", "nameOfDivision", divisionId);
                  handleChange("formatIV", "nameOfDivision", divisionId);

                  // Fetch ranges
                  fetchRanges(divisionId);
                }}
              >
                <option value="">Select Division</option>
                {divisions.map((div) => (
                  <option key={div._id} value={div._id}>
                    {div.divisionName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Range</Form.Label>
              <Form.Select
                value={formData.range}
                onChange={(e) => {
                  handleChange("", "range", e.target.value);
                  fetchVfcs(e.target.value);
                }}
              >
                <option value="">Select Range</option>
                {ranges.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.rangeName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Render Format Form */}
        {selectedFormat === "FORMAT-I" && (
          <FormatIForm
            ref={formatRefs["FORMAT-I"]}
            formData={formData}
            setFormData={setFormData}
            vfcs={vfcs}
          />
        )}
        {selectedFormat === "FORMAT-II" && (
          <FormatIIForm
            ref={formatRefs["FORMAT-II"]}
            formData={formData}
            setFormData={setFormData}
          />
        )}
        {selectedFormat === "FORMAT-III" && (
          <FormatIIIForm
            ref={formatRefs["FORMAT-III"]}
            formData={formData}
            setFormData={setFormData}
            vfcs={vfcs}
          />
        )}
        {selectedFormat === "FORMAT-III(A)" && (
          <FormatIIIAForm
            ref={formatRefs["FORMAT-III(A)"]}
            formData={formData}
            setFormData={setFormData}
            vfcs={vfcs}
          />
        )}
        {selectedFormat === "FORMAT-IV" && (
          <FormatIVForm
            ref={formatRefs["FORMAT-IV"]}
            formData={formData}
            setFormData={setFormData}
            vfcs={vfcs}
          />
        )}

        {/* Action Buttons */}
        <div className="mt-4 d-flex gap-3">
          {!savedSteps.includes(selectedFormat) && (
            <Button variant="success" onClick={handleSave}>
              Save
            </Button>
          )}

          {savedSteps.includes(selectedFormat) &&
            currentStep < formatSteps.length - 1 && (
              <Button variant="primary" onClick={handleContinue}>
                Continue
              </Button>
            )}

          {savedSteps.length === formatSteps.length &&
            currentStep === formatSteps.length - 1 && (
              <Button type="submit" variant="primary">
                Submit Report
              </Button>
            )}
        </div>
      </Form>
    </div>
  );
};

export default HalfYearlyReportForm;
